import { useQuery } from '@tanstack/react-query';
import { getMenu, type MenuResponse } from '@/api/modulos.api';
import { useWorkspaceStore } from '@/store/workspace.store';

/**
 * FASE 4 (El Edificio) — el menú del negocio activo.
 *
 * La clave de la consulta lleva el negocio activo a propósito: al cambiar de
 * inquilino, el menú tiene que volver a pedirse. Dos negocios con módulos
 * distintos no comparten menú, y servir el anterior mientras llega el nuevo es
 * exactamente el bug del panel que muestra un cliente encima de los datos de
 * otro (trampa 03 de `silva-service-360`).
 */
export interface EstadoDelMenu {
  datos: MenuResponse | undefined;
  cargando: boolean;
  /** `true` si el servidor no pudo responder. El panel degrada, no se rompe. */
  fallo: boolean;
  /**
   * `true` cuando todavía no hay negocio activo.
   *
   * Es un estado DISTINTO de "cargando", y la diferencia no es cosmética: ver
   * `sinNegocio` abajo.
   */
  sinNegocio: boolean;
}

export function useMenu(): EstadoDelMenu {
  const negocioActivo = useWorkspaceStore((s) => s.activeId);
  const workspacesCargados = useWorkspaceStore((s) => s.cargado);

  const { data, isPending, isError } = useQuery({
    queryKey: ['menu', negocioActivo],
    queryFn: getMenu,
    // El menú cambia cuando alguien activa un módulo, que es raro. Cinco
    // minutos evita pedirlo en cada navegación sin dejarlo viejo.
    staleTime: 5 * 60 * 1000,
    // Sin negocio activo no hay menú que pedir: el servidor respondería 403
    // pidiendo que elija.
    enabled: Boolean(negocioActivo),
  });

  // ═══ EL BUG QUE ESTO ARREGLA ═══
  //
  // En React Query v5 una consulta con `enabled: false` se queda en
  // `isPending: true` PARA SIEMPRE: nunca corrió, así que nunca deja de estar
  // pendiente. Devolver eso como `cargando` dejaba el sidebar con "Cargando el
  // menú…" eternamente cuando no había negocio activo.
  //
  // Lo grave no es el spinner: es que TAPABA el problema real. Si la lista de
  // negocios no llega (sesión vencida, servidor caído, usuario sin membresías),
  // el panel se veía "cargando" en vez de decir qué pasaba, y no hay forma de
  // distinguir eso de una red lenta. Se vio en producción el 19 de septiembre.
  //
  // Ahora son tres estados y cada uno dice algo distinto:
  //   sinNegocio  no hay dónde mirar todavía
  //   cargando    se está pidiendo de verdad
  //   fallo       el servidor contestó que no
  const sinNegocio = !negocioActivo;

  return {
    datos: data,
    // Solo es "cargando" si de verdad hay una consulta en vuelo. Mientras los
    // workspaces todavía no llegaron, también: ahí sí se está esperando algo.
    cargando: sinNegocio ? !workspacesCargados : isPending,
    fallo: isError,
    // Ya llegó la lista de negocios y aun así no hay ninguno activo: eso no es
    // una carga, es un problema que hay que nombrar.
    sinNegocio: sinNegocio && workspacesCargados,
  };
}

/**
 * ¿Existe esta pantalla para quien mira? Lo decide el servidor (módulos, rol y, desde el
 * 7-oct, si es la administración del sistema). Mientras el menú carga, no.
 */
export function useVeRuta(ruta: string): boolean {
  return useMenu().datos?.rutas.some((r) => r.ruta === ruta) ?? false;
}
